import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ScanLine, Loader2, AlertTriangle, RefreshCw } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { supabase } from '../../../lib/supabaseClient'

// Recurring-stack AI audit on /finance. One tap runs the
// finance-subscription-audit function (deterministic totals + LLM
// commentary/flags); the last result is cached locally so the card
// shows something useful on every visit without burning a call.

const CACHE_KEY = 'phoenix:subscription-audit'

export default function SubscriptionAuditCard() {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [ranAt, setRanAt] = useState(null)

  useEffect(() => {
    try {
      const cached = JSON.parse(window.localStorage.getItem(CACHE_KEY) || 'null')
      if (cached?.result) { setResult(cached.result); setRanAt(cached.ranAt) }
    } catch { /* corrupt cache — ignore */ }
  }, [])

  const run = async () => {
    if (busy) return
    setBusy(true)
    const { data, error } = await supabase.functions.invoke('finance-subscription-audit', { body: {} })
    setBusy(false)
    if (error || !data) { setResult({ failed: true }); return }
    const ts = new Date().toISOString().slice(0, 10)
    setResult(data)
    setRanAt(ts)
    try { window.localStorage.setItem(CACHE_KEY, JSON.stringify({ result: data, ranAt: ts })) } catch { /* full */ }
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-2.5">
        <div className="flex items-center gap-2">
          <h2 className="flex-1 min-w-0 text-sm font-semibold flex items-center gap-1.5 truncate" dir="auto">
            <ScanLine className="w-4 h-4 text-app-primary-500" />
            {t('finance.audit.title')}
          </h2>
          <Button size="sm" variant="outline" onClick={run} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 me-1" />}
            {!busy && (result ? t('finance.audit.rerun') : t('finance.audit.run'))}
          </Button>
        </div>

        {!result && !busy && (
          <p className="text-xs text-muted-foreground" dir="auto">{t('finance.audit.hint')}</p>
        )}

        {result?.failed && (
          <p className="text-xs text-red-500" dir="auto">{t('finance.audit.failed')}</p>
        )}

        {result?.empty && (
          <p className="text-sm text-muted-foreground" dir="auto">{t('finance.audit.empty')}</p>
        )}

        {result?.totals && !result.empty && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" dir="auto">
            <span>
              {t('finance.audit.recurringTotal')}{' '}
              <b className="text-foreground" dir="ltr">{Number(result.totals.recurring_total).toLocaleString('en-US')} SAR</b>
            </span>
            {result.totals.recurring_pct_of_income != null && (
              <span>
                {t('finance.audit.ofIncome')}{' '}
                <b className="text-foreground" dir="ltr">{result.totals.recurring_pct_of_income}%</b>
              </span>
            )}
          </div>
        )}

        {result?.commentary && (
          <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed" dir="auto">
            {result.commentary}
          </p>
        )}

        {result?.flags?.length > 0 && (
          <div className="space-y-1.5">
            {result.flags.map((f, i) => (
              <div key={i} className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-2.5 py-2" dir="auto">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-foreground min-w-0">
                  <b>{f.name}</b> — {f.issue}
                </p>
              </div>
            ))}
          </div>
        )}

        {ranAt && result && !result.failed && (
          <p className="text-[10px] text-muted-foreground" dir="ltr">{ranAt}</p>
        )}
      </CardContent>
    </Card>
  )
}
